import type {
  CompanyFinanceRollupSummary,
  FinanceConfidenceReason,
  FinanceDataReliability,
  FinanceDepartmentAverageRates,
  FinanceRollupStageTotals,
  FinanceRollupSummary,
  Task,
  TaskFinancialStage,
  TaskFinancialState,
  TimeLog,
} from "../models/production";
import type { AssetRateCard, Person, ProjectSetup, RateCard } from "../types";
import type { HierarchySnapshot } from "../shared/rollupTypes";
import { computeInitialTaskFinancialState, MINUTES_PER_DAY } from "./financeStageEngine";

const STAGE_ORDER: TaskFinancialStage[] = [
  "initial_bid_forecast",
  "assigned_artist_forecast",
  "in_progress_on_target",
  "in_progress_over_schedule",
  "final_actual",
];

export interface FinanceComputationContext {
  setup?: Pick<ProjectSetup, "currency" | "financeCurrency" | "financeDepartmentAverageRates" | "productionCompanyId" | "clientName">;
  people?: Array<Pick<Person, "id" | "name" | "fullName" | "displayName" | "dailyRate">>;
  timeLogs?: TimeLog[];
  shotRates?: Partial<RateCard> | null;
  assetRates?: Partial<AssetRateCard> | null;
  departmentAverageRates?: FinanceDepartmentAverageRates | Record<string, number>;
  now?: string;
}

export interface FinanceDashboardComputationResult {
  tasks: Record<string, TaskFinancialState>;
  shots: Record<string, FinanceRollupSummary>;
  assets: Record<string, FinanceRollupSummary>;
  sequences: Record<string, FinanceRollupSummary>;
  episodes: Record<string, FinanceRollupSummary>;
  projects: Record<string, FinanceRollupSummary>;
  companies: Record<string, CompanyFinanceRollupSummary>;
}

function roundCurrency(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 100) / 100;
}

function roundDays(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 1000) / 1000;
}

function roundMargin(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 10) / 10;
}

function calculateMarginPct(grossProfit?: number, bidValue?: number) {
  if (typeof grossProfit !== "number" || typeof bidValue !== "number" || !Number.isFinite(grossProfit) || !Number.isFinite(bidValue) || bidValue <= 0) {
    return undefined;
  }
  return roundMargin((grossProfit / bidValue) * 100);
}

function sumStageTotals(items: FinanceRollupStageTotals[]): FinanceRollupStageTotals {
  const bidValue = items.reduce((sum, item) => sum + (item.bidValue || 0), 0);
  const labourCost = items.reduce((sum, item) => sum + (item.labourCost || 0), 0);
  const grossProfit = items.reduce((sum, item) => sum + (item.grossProfit || 0), 0);
  const taskCount = items.reduce((sum, item) => sum + (item.taskCount || 0), 0);

  return {
    bidValue: roundCurrency(bidValue),
    labourCost: roundCurrency(labourCost),
    grossProfit: roundCurrency(grossProfit),
    grossMarginPct: calculateMarginPct(grossProfit, bidValue),
    taskCount,
  };
}

function selectRollupConfidenceReasons(taskStates: TaskFinancialState[]): FinanceConfidenceReason[] {
  const reasonCounts = new Map<string, { reason: FinanceConfidenceReason; count: number }>();
  for (const state of taskStates) {
    for (const reason of state.confidenceReasons || []) {
      const current = reasonCounts.get(reason.code);
      reasonCounts.set(reason.code, {
        reason,
        count: (current?.count || 0) + 1,
      });
    }
  }

  return Array.from(reasonCounts.values())
    .sort((a, b) => b.count - a.count || a.reason.label.localeCompare(b.reason.label))
    .slice(0, 3)
    .map((entry) => entry.reason);
}

function normalizeDepartmentKey(value?: string | null) {
  return String(value || "").trim();
}

function getDepartmentAverageRate(task: Task, context: FinanceComputationContext) {
  const inlineRates = context.setup?.financeDepartmentAverageRates || {};
  const rawDepartmentAverageRates = context.departmentAverageRates;
  const wrappedRates =
    rawDepartmentAverageRates &&
    typeof rawDepartmentAverageRates === "object" &&
    "ratesByDepartment" in rawDepartmentAverageRates &&
    typeof (rawDepartmentAverageRates as { ratesByDepartment?: unknown }).ratesByDepartment === "object"
      ? ((rawDepartmentAverageRates as { ratesByDepartment?: Record<string, number> }).ratesByDepartment || {})
      : null;
  const configuredRates: Record<string, number> = wrappedRates || ((rawDepartmentAverageRates as Record<string, number> | undefined) || {});

  const key = normalizeDepartmentKey(task.taskType);
  const mergedRates = {
    ...configuredRates,
    ...inlineRates,
  };

  const configuredRate = mergedRates[key];
  if (typeof configuredRate === "number" && Number.isFinite(configuredRate)) {
    return configuredRate;
  }
  return undefined;
}

function getTaskSellRate(task: Task, context: FinanceComputationContext) {
  const key = normalizeDepartmentKey(task.taskType);
  if (!key) return undefined;
  if (task.parentEntityType === "asset") {
    const rate = context.assetRates?.[key as keyof AssetRateCard];
    return typeof rate === "number" && Number.isFinite(rate) ? rate : undefined;
  }
  const rate = context.shotRates?.[key as keyof RateCard];
  return typeof rate === "number" && Number.isFinite(rate) ? rate : undefined;
}

function getTaskBidDays(task: Task) {
  if (typeof task.finance?.bidTimeAllocationDays === "number" && Number.isFinite(task.finance.bidTimeAllocationDays)) {
    return task.finance.bidTimeAllocationDays;
  }
  // Legacy note: bidHours currently stores the app's bid day allocation.
  if (typeof task.bidHours === "number" && Number.isFinite(task.bidHours)) {
    return task.bidHours;
  }
  if (typeof task.finance?.bidTimeAllocationMinutes === "number" && Number.isFinite(task.finance.bidTimeAllocationMinutes)) {
    return task.finance.bidTimeAllocationMinutes / MINUTES_PER_DAY;
  }
  return 0;
}

function getTaskBidMinutes(task: Task) {
  if (typeof task.finance?.bidTimeAllocationMinutes === "number" && Number.isFinite(task.finance.bidTimeAllocationMinutes)) {
    return task.finance.bidTimeAllocationMinutes;
  }
  return Math.round(getTaskBidDays(task) * MINUTES_PER_DAY);
}

function getTaskBidValueInMoney(task: Task, context: FinanceComputationContext) {
  if (typeof task.finance?.taskBidValueInMoney === "number" && Number.isFinite(task.finance.taskBidValueInMoney)) {
    return task.finance.taskBidValueInMoney;
  }

  const sellRate = getTaskSellRate(task, context);
  const bidDays = getTaskBidDays(task);
  if (typeof sellRate === "number" && bidDays > 0) {
    return sellRate * bidDays;
  }
  return undefined;
}

export function computeTaskFinancialState(task: Task, context: FinanceComputationContext = {}): TaskFinancialState {
  return computeInitialTaskFinancialState(task, context, task.finance).nextState;
}

function summarizeTaskStates(taskStates: TaskFinancialState[], currency?: string): FinanceRollupSummary {
  const stageCounts = taskStates.reduce<Partial<Record<TaskFinancialStage, number>>>((counts, state) => {
    if (state.financialStage) {
      counts[state.financialStage] = (counts[state.financialStage] || 0) + 1;
    }
    return counts;
  }, {});

  const financeReliability: FinanceDataReliability =
    taskStates.some((state) => state.financeReliability === "low")
      ? "low"
      : taskStates.some((state) => state.financeReliability === "medium")
        ? "medium"
        : "high";

  const totalBidValueInMoney = taskStates.reduce((sum, state) => sum + (state.taskBidValueInMoney || 0), 0);
  const totalBidTimeAllocationMinutes = taskStates.reduce((sum, state) => sum + (state.bidTimeAllocationMinutes || 0), 0);
  const totalLoggedMinutes = taskStates.reduce((sum, state) => sum + (state.actualLoggedMinutes || 0), 0);
  const totalForecastLabourCost = taskStates.reduce((sum, state) => sum + (state.forecastLabourCost || 0), 0);
  const totalActualLabourCost = taskStates.reduce((sum, state) => sum + (state.actualLabourCost || 0), 0);
  const totalForecastGrossProfit = taskStates.reduce((sum, state) => sum + (state.forecastGrossProfit || 0), 0);
  const totalActualGrossProfit = taskStates.reduce((sum, state) => sum + (state.actualGrossProfit || 0), 0);
  const varianceVsBidMinutes = totalLoggedMinutes - totalBidTimeAllocationMinutes;
  const varianceVsBidCost = taskStates.reduce((sum, state) => sum + (state.varianceVsBidCost || 0), 0);
  const marginDeltaFromPreviousStage = taskStates.reduce((sum, state) => sum + (state.marginDeltaFromPreviousStage || 0), 0);
  const completedTaskCount = taskStates.filter((state) => state.financialStage === "final_actual").length;
  const onTargetTaskCount = taskStates.filter((state) => state.financialStage === "in_progress_on_target").length;
  const overScheduleTaskCount = taskStates.filter((state) => state.financialStage === "in_progress_over_schedule").length;
  const missingRateCoverageTaskCount = taskStates.filter((state) => state.missingRateCoverage).length;
  const missingTimelogConfidenceTaskCount = taskStates.filter((state) => state.missingTimelogCoverage).length;
  const missingConfidenceTaskCount = taskStates.filter(
    (state) => state.financeReliability !== "high" || state.missingRateCoverage || state.missingTimelogCoverage
  ).length;

  const baseline = sumStageTotals(
    taskStates.map((state) => {
      const snapshot = state.stageSnapshots?.find((entry) => entry.stage === "initial_bid_forecast");
      return {
        bidValue: state.taskBidValueInMoney,
        labourCost: snapshot?.labourCost,
        grossProfit: snapshot?.grossProfit,
        taskCount: 1,
      };
    })
  );

  const currentForecast = sumStageTotals(
    taskStates.map((state) => ({
      bidValue: state.taskBidValueInMoney,
      labourCost: state.forecastLabourCost,
      grossProfit: state.forecastGrossProfit,
      taskCount: 1,
    }))
  );

  const finalActual = sumStageTotals(
    taskStates
      .filter((state) => state.financialStage === "final_actual")
      .map((state) => ({
        bidValue: state.taskBidValueInMoney,
        labourCost: state.actualLabourCost,
        grossProfit: state.actualGrossProfit,
        taskCount: 1,
      }))
  );

  const blendedLive = sumStageTotals(
    taskStates.map((state) => {
      const useActual =
        state.financialStage === "in_progress_on_target" ||
        state.financialStage === "in_progress_over_schedule" ||
        state.financialStage === "final_actual";
      return {
        bidValue: state.taskBidValueInMoney,
        labourCost: useActual ? state.actualLabourCost ?? state.forecastLabourCost : state.forecastLabourCost,
        grossProfit: useActual ? state.actualGrossProfit ?? state.forecastGrossProfit : state.forecastGrossProfit,
        taskCount: 1,
      };
    })
  );

  const reliabilityScoreTotal = taskStates.reduce((sum, state) => {
    if (state.financeReliability === "high") return sum + 1;
    if (state.financeReliability === "medium") return sum + 0.65;
    return sum + 0.3;
  }, 0);
  const financialConfidenceScore =
    taskStates.length > 0 ? Math.round((reliabilityScoreTotal / taskStates.length) * 100) : undefined;
  const averageMarginPct = blendedLive.grossMarginPct;
  const confidenceReasons = selectRollupConfidenceReasons(taskStates);
  const forecastLabel = "Current Forecast";
  const forecastHelperText = "Forecast totals combine assumption-driven tasks with live in-progress tasks.";
  const actualLabel = finalActual.taskCount ? "Final Actual" : blendedLive.taskCount ? "Blended Live" : "Actual Pending";
  const actualHelperText = finalActual.taskCount
    ? "Completed tasks contribute final actual labour cost and gross profit."
    : "Actual totals are still mixed with forecast assumptions because some tasks are not complete.";

  return {
    currency,
    totalTaskCount: taskStates.length,
    completedTaskCount,
    onTargetTaskCount,
    overScheduleTaskCount,
    missingConfidenceTaskCount,
    missingRateCoverageTaskCount,
    missingTimelogConfidenceTaskCount,
    averageMarginPct,
    financialConfidenceScore,
    totalBidValueInMoney: roundCurrency(totalBidValueInMoney),
    totalBidTimeAllocationMinutes,
    totalBidTimeAllocationDays: roundDays(totalBidTimeAllocationMinutes / MINUTES_PER_DAY),
    totalLoggedMinutes,
    totalLoggedDays: roundDays(totalLoggedMinutes / MINUTES_PER_DAY),
    totalForecastLabourCost: roundCurrency(totalForecastLabourCost),
    totalActualLabourCost: roundCurrency(totalActualLabourCost),
    totalForecastGrossProfit: roundCurrency(totalForecastGrossProfit),
    totalActualGrossProfit: roundCurrency(totalActualGrossProfit),
    varianceVsBidMinutes,
    varianceVsBidCost: roundCurrency(varianceVsBidCost),
    marginDeltaFromPreviousStage: roundMargin(marginDeltaFromPreviousStage),
    financeReliability,
    confidenceReasons,
    provisional: taskStates.some((state) => state.provisional),
    forecastLabel,
    forecastHelperText,
    actualLabel,
    actualHelperText,
    taskStageCounts: stageCounts,
    baseline,
    currentForecast,
    finalActual,
    blendedLive,
    lastComputedAt: taskStates.map((state) => state.lastComputedAt).filter(Boolean).sort().at(-1),
    note:
      financeReliability === "low"
        ? "Rollup contains significant rate or timelog gaps"
        : financeReliability === "medium"
          ? "Rollup is partially forecast-based"
          : undefined,
  };
}

function rollupTasksByKey(
  tasks: Task[],
  taskStates: Record<string, TaskFinancialState>,
  getKey: (task: Task) => string | undefined,
  currency?: string
) {
  const grouped = new Map<string, TaskFinancialState[]>();

  for (const task of tasks) {
    const key = getKey(task);
    if (!key) continue;
    const state = taskStates[task.id];
    if (!state) continue;
    grouped.set(key, (grouped.get(key) || []).concat(state));
  }

  return Array.from(grouped.entries()).reduce<Record<string, FinanceRollupSummary>>((acc, [key, states]) => {
    acc[key] = summarizeTaskStates(states, currency);
    return acc;
  }, {});
}

export function computeFinanceDashboard(snapshot: HierarchySnapshot, context: FinanceComputationContext = {}): FinanceDashboardComputationResult {
  const now = context.now || new Date().toISOString();
  const currency = context.setup?.financeCurrency || context.setup?.currency;
  const tasks = snapshot.tasks || [];
  const tasksById = tasks.reduce<Record<string, TaskFinancialState>>((acc, task) => {
    acc[task.id] = computeTaskFinancialState(task, { ...context, now });
    return acc;
  }, {});

  return {
    tasks: tasksById,
    shots: rollupTasksByKey(tasks, tasksById, (task) => task.shotId || (task.parentEntityType === "shot" ? task.parentEntityId : undefined), currency),
    assets: rollupTasksByKey(tasks, tasksById, (task) => task.assetId || (task.parentEntityType === "asset" ? task.parentEntityId : undefined), currency),
    sequences: rollupTasksByKey(tasks, tasksById, (task) => task.sequenceId, currency),
    episodes: rollupTasksByKey(tasks, tasksById, (task) => task.episodeId, currency),
    projects: rollupTasksByKey(tasks, tasksById, (task) => task.projectId, currency),
    companies: (() => {
      const projectStates = Object.values(rollupTasksByKey(tasks, tasksById, (task) => task.projectId, currency));
      const companyId = context.setup?.productionCompanyId || "unassigned_company";
      const companyName = context.setup?.clientName;
      return {
        [companyId]: {
          ...summarizeTaskStates(Object.values(tasksById), currency),
          companyId,
          companyName,
          projectIds: Array.from(new Set(tasks.map((task) => task.projectId))),
        },
      };
    })(),
  };
}
